import React, { useState, useRef } from 'react';
import { 
  User, Mail, Phone, Building2, Scale, MapPin, 
  Image as ImageIcon, ArrowRight, Check, 
  AlertCircle, Upload, X, Loader2, ChevronLeft, ChevronRight, Eye, EyeOff, Info
} from 'lucide-react';
import { useGoogleLogin } from '@react-oauth/google';
import axios from 'axios';
import { useLanguage } from '../../context/LanguageContext';
import { useNavigate } from 'react-router-dom';
import { ROUTES } from '../../constants/routes.constants';
import Logo from '../common/Logo';
import Wizard from '../wizard/Wizard';
import { extractCountryCode } from '../../utils/phoneUtils';

const API_BASE_URL = ROUTES.API_BASE_URL;

const AgencyRegistration = () => {
  const { t } = useLanguage();
  const navigate = useNavigate();
  
  // Wizard state
  const [currentStep, setCurrentStep] = useState(0);
  
  // Form data state (persists across steps)
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    companyName: '',
    email: '',
    phoneNumber: '',
    countryCode: '+212',
    password: '',
    confirmPassword: '',
    legalForm: '',
    address: '',
    logo: null,
    logoPreview: null,
    meetingDate: '',
    meetingTime: '',
  });
  const [errors, setErrors] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [acceptedTerms, setAcceptedTerms] = useState(false);
  const [isGoogleLoading, setIsGoogleLoading] = useState(false);
  const [googleSuccessMessage, setGoogleSuccessMessage] = useState('');
  const [highlightPhone, setHighlightPhone] = useState(false);
  const [phoneError, setPhoneError] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [passwordError, setPasswordError] = useState(false);
  const [confirmPasswordError, setConfirmPasswordError] = useState(false);
  const fileInputRef = useRef(null);
  const phoneInputRef = useRef(null);

  const legalForms = [
    { value: 'SARL', label: 'SARL (Société à Responsabilité Limitée)' },
    { value: 'SA', label: 'SA (Société Anonyme)' },
    { value: 'SNC', label: 'SNC (Société en Nom Collectif)' },
    { value: 'EURL', label: 'EURL (Entreprise Unipersonnelle à Responsabilité Limitée)' },
    { value: 'AUTO_ENTREPRENEUR', label: 'Auto-entrepreneur' },
    { value: 'OTHER', label: 'Autre' },
  ];

  // Wizard steps configuration
  const steps = [
    {
      id: 'manager',
      title: 'Identité du Gérant',
      description: 'Informations personnelles du responsable',
    },
    {
      id: 'agency',
      title: 'Informations Agence',
      description: 'Détails sur votre entreprise',
    },
    {
      id: 'scheduling',
      title: 'Appel de Validation',
      description: 'Choisissez un créneau pour votre appel',
    },
    {
      id: 'review',
      title: 'Révision & Envoi',
      description: 'Vérifiez vos informations avant envoi',
    },
  ];

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
    // Réinitialiser les erreurs de mot de passe quand l'utilisateur tape
    if (name === 'password' && passwordError) {
      setPasswordError(false);
    }
    // Réinitialiser l'erreur de correspondance quand l'utilisateur tape dans la confirmation
    if (name === 'confirmPassword' && confirmPasswordError) {
      setConfirmPasswordError(false);
    }
  };
  

  const handlePhoneChange = (e) => {
    const val = e.target.value;
    
    // Allow only numbers and spaces
    if (!/^[0-9\s]*$/.test(val)) return;
    
    // Réinitialiser la mise en évidence quand l'utilisateur commence à taper
    if (highlightPhone) {
      setHighlightPhone(false);
    }
    
    // FORCE RESET ERROR - Réinitialiser l'erreur immédiatement à chaque frappe
    if (phoneError) {
      setPhoneError(false);
    }
    
    // Mettre à jour les données du formulaire
    setFormData(prev => ({
      ...prev,
      phoneNumber: val
    }));
  };
  
  const handlePhoneBlur = () => {
    // Valider uniquement quand l'utilisateur quitte le champ
    const cleanNumber = formData.phoneNumber.replace(/\s/g, '');
    if (cleanNumber.length > 0 && cleanNumber.length !== 9) {
      setPhoneError(true);
    } else {
      setPhoneError(false);
    }
  };

  // Fonction helper pour valider le téléphone (exactement 9 chiffres)
  const isValidPhone = (phoneStr) => {
    if (!phoneStr) return false;
    // Remove all non-digit characters (spaces, dashes, etc.) to count only digits
    const cleaned = phoneStr.replace(/\D/g, '');
    // Check if length is exactly 9
    return cleaned.length === 9;
  };

  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 5 * 1024 * 1024) { // 5MB max
        setErrors(prev => ({
          ...prev,
          logo: t('agencyRegistration.logoSizeError')
        }));
        return;
      }
      
      if (!file.type.startsWith('image/')) {
        setErrors(prev => ({
          ...prev,
          logo: t('agencyRegistration.logoFormatError')
        }));
        return;
      }

      const reader = new FileReader();
      reader.onloadend = () => {
        setFormData(prev => ({
          ...prev,
          logo: file,
          logoPreview: reader.result
        }));
      };
      reader.readAsDataURL(file);
      
      if (errors.logo) {
        setErrors(prev => ({
          ...prev,
          logo: ''
        }));
      }
    }
  };

  const removeLogo = () => {
    setFormData(prev => ({
      ...prev,
      logo: null,
      logoPreview: null
    }));
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  // Google Auto-Fill Logic
  const fetchGoogleUserInfo = async (accessToken) => {
    try {
      const response = await axios.get(
        'https://www.googleapis.com/oauth2/v3/userinfo',
        {
          headers: {
            Authorization: `Bearer ${accessToken}`,
          },
        }
      );

      const { given_name, family_name, email } = response.data;

      // Mettre à jour le formulaire avec les données Google
      setFormData(prev => ({
        ...prev,
        firstName: given_name || prev.firstName,
        lastName: family_name || prev.lastName,
        email: email || prev.email,
      }));

      // Effacer les erreurs pour ces champs
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors.firstName;
        delete newErrors.lastName;
        delete newErrors.email;
        return newErrors;
      });

      // Afficher un message de succès
      setGoogleSuccessMessage('Informations Google remplies avec succès !');
      setTimeout(() => {
        setGoogleSuccessMessage('');
      }, 3000);

      // Mettre en évidence le champ téléphone (car il reste vide)
      setHighlightPhone(true);

      console.log('✅ Google Auto-Fill Success:', { given_name, family_name, email });
    } catch (error) {
      console.error('❌ Google Auto-Fill Error:', error);
      setErrors(prev => ({
        ...prev,
        googleAutoFill: 'Erreur lors de la récupération des informations Google',
      }));
    } finally {
      setIsGoogleLoading(false);
    }
  };

  const handleGoogleAutoFill = useGoogleLogin({
    onSuccess: (tokenResponse) => {
      setIsGoogleLoading(true);
      // Récupérer les informations utilisateur avec le token d'accès
      fetchGoogleUserInfo(tokenResponse.access_token);
    },
    onError: (error) => {
      console.error('Google Login Error:', error);
      setIsGoogleLoading(false);
      setErrors(prev => ({
        ...prev,
        googleAutoFill: 'Erreur lors de la connexion Google',
      }));
    },
    scope: 'openid profile email',
  });

  // Validation par étape (read-only, ne modifie pas l'état)
  const isStepValid = (stepIndex) => {
    if (stepIndex === 0) {
      // Step 1: Identité du Gérant
      return (
        formData.firstName.trim() !== '' &&
        formData.lastName.trim() !== '' &&
        formData.email.trim() !== '' &&
        /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email) &&
        formData.phoneNumber.trim() !== '' &&
        formData.password.trim() !== '' &&
        formData.password.length >= 8 &&
        formData.confirmPassword.trim() !== '' &&
        formData.password === formData.confirmPassword
      );
    } else if (stepIndex === 1) {
      // Step 2: Informations Agence
      return (
        formData.companyName.trim() !== '' &&
        formData.legalForm !== '' &&
        formData.address.trim() !== ''
      );
    } else if (stepIndex === 2) {
      // Step 3: Appel de Validation
      return (
        formData.meetingDate !== '' &&
        formData.meetingTime !== ''
      );
    }
    return true;
  };

  // Validation par étape (avec mise à jour des erreurs)
  const validateStep = (stepIndex) => {
    const newErrors = {};
    
    if (stepIndex === 0) {
      // Step 1: Identité du Gérant
      if (!formData.firstName.trim()) {
        newErrors.firstName = t('agencyRegistration.firstNameRequired');
      }
      if (!formData.lastName.trim()) {
        newErrors.lastName = t('agencyRegistration.lastNameRequired');
      }
      if (!formData.email.trim()) {
        newErrors.email = t('agencyRegistration.emailRequired');
      } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
        newErrors.email = t('agencyRegistration.emailInvalid');
      }
      // Note: Le téléphone n'est pas validé ici car il est vérifié uniquement au onBlur
      if (!formData.password.trim()) {
        newErrors.password = 'Le mot de passe est requis';
      } else if (formData.password.length < 8) {
        newErrors.password = 'Le mot de passe doit contenir au moins 8 caractères';
      }
      if (!formData.confirmPassword.trim()) {
        newErrors.confirmPassword = 'La confirmation du mot de passe est requise';
      } else if (formData.password !== formData.confirmPassword) {
        newErrors.confirmPassword = 'Les mots de passe ne correspondent pas';
      }
    } else if (stepIndex === 1) {
      // Step 2: Informations Agence
      if (!formData.companyName.trim()) {
        newErrors.companyName = t('agencyRegistration.companyNameRequired');
      }
      if (!formData.legalForm) {
        newErrors.legalForm = t('agencyRegistration.legalFormRequired');
      }
      if (!formData.address.trim()) {
        newErrors.address = t('agencyRegistration.addressRequired');
      }
    } else if (stepIndex === 2) {
      // Step 3: Appel de Validation
      if (!formData.meetingDate) {
        newErrors.meetingDate = 'Veuillez sélectionner une date';
      }
      if (!formData.meetingTime) {
        newErrors.meetingTime = 'Veuillez sélectionner un horaire';
      }
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Validation complète pour la soumission finale
  const validateForm = () => {
    const newErrors = {};
    
    if (!formData.firstName.trim()) {
      newErrors.firstName = t('agencyRegistration.firstNameRequired');
    }
    if (!formData.lastName.trim()) {
      newErrors.lastName = t('agencyRegistration.lastNameRequired');
    }
    if (!formData.companyName.trim()) {
      newErrors.companyName = t('agencyRegistration.companyNameRequired');
    }
    if (!formData.email.trim()) {
      newErrors.email = t('agencyRegistration.emailRequired');
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = t('agencyRegistration.emailInvalid');
    }
    if (!formData.phoneNumber.trim()) {
      newErrors.phoneNumber = t('agencyRegistration.phoneRequired');
    }
    if (!formData.legalForm) {
      newErrors.legalForm = t('agencyRegistration.legalFormRequired');
    }
    if (!formData.address.trim()) {
      newErrors.address = t('agencyRegistration.addressRequired');
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Navigation entre les étapes
  const handleNext = () => {
    // Validation pour l'étape 1 (Identité du Gérant)
    if (currentStep === 0) {
      // Valider tous les champs et afficher les erreurs (SAUF le téléphone qui est vérifié au onBlur)
      const isValid = validateStep(currentStep);
      
      // Validation supplémentaire pour les mots de passe
      const isPasswordEmpty = !formData.password?.trim();
      const isPasswordTooShort = formData.password && formData.password.length < 8;
      const isConfirmPasswordEmpty = !formData.confirmPassword?.trim();
      const doPasswordsMatch = formData.password === formData.confirmPassword;
      
      // Afficher les erreurs de mot de passe si nécessaire
      if (isPasswordEmpty || isPasswordTooShort) {
        setPasswordError(true);
      }
      
      if (isConfirmPasswordEmpty || !doPasswordsMatch) {
        setConfirmPasswordError(true);
        // Afficher l'erreur de correspondance si les mots de passe ne correspondent pas
        if (!doPasswordsMatch && !isConfirmPasswordEmpty) {
          setErrors(prev => ({
            ...prev,
            confirmPassword: 'Les mots de passe ne correspondent pas'
          }));
        }
      }
      
      // Si tout est valide (sauf le téléphone qui est vérifié au onBlur), passer à l'étape suivante
      // Note: Le téléphone n'est pas vérifié ici car il est vérifié uniquement au onBlur
      if (isValid && !isPasswordEmpty && !isPasswordTooShort && !isConfirmPasswordEmpty && doPasswordsMatch) {
        setCurrentStep(prev => Math.min(prev + 1, steps.length - 1));
        // Réinitialiser les erreurs de mot de passe si tout est valide
        setPasswordError(false);
        setConfirmPasswordError(false);
      }
      // Sinon, les erreurs sont déjà affichées, on reste sur la même étape
    } else {
      // Pour les autres étapes, valider et avancer si valide
      if (validateStep(currentStep)) {
        setCurrentStep(prev => Math.min(prev + 1, steps.length - 1));
      }
    }
  };

  const handlePrevious = () => {
    setCurrentStep(prev => Math.max(prev - 1, 0));
  };

  const handleEditPhone = () => {
    setCurrentStep(0); // Retourner à l'étape "Identité du Gérant" où se trouve le champ téléphone
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();
    
    // Valider toutes les étapes avant soumission
    if (!validateStep(0) || !validateStep(1) || !validateStep(2)) {
      // Aller à la première étape avec erreur
      if (!validateStep(0)) {
        setCurrentStep(0);
      } else if (!validateStep(1)) {
        setCurrentStep(1);
      } else if (!validateStep(2)) {
        setCurrentStep(2);
      }
      return;
    }
    
    setIsLoading(true);
    
    try {
      // Extract phone number parts using centralized utility
      const { countryCode, phoneNumber: phoneNumberOnly } = extractCountryCode(
        formData.phoneNumber,
        formData.countryCode || '+212'
      );
      
      // Create FormData for file upload
      const submitData = new FormData();
      submitData.append('firstName', formData.firstName);
      submitData.append('lastName', formData.lastName);
      submitData.append('companyName', formData.companyName);
      submitData.append('email', formData.email);
      submitData.append('phoneNumber', phoneNumberOnly);
      submitData.append('countryCode', countryCode);
      submitData.append('legalForm', formData.legalForm);
      submitData.append('address', formData.address);
      submitData.append('meetingDate', formData.meetingDate);
      submitData.append('meetingTime', formData.meetingTime);
      if (formData.logo) {
        submitData.append('logo', formData.logo);
      }
      
      const response = await fetch(`${API_BASE_URL}/auth/agency/register`, {
        method: 'POST',
        body: submitData,
      });
      
      const data = await response.json();
      
      if (!response.ok) {
        // Handle field-specific errors from backend
        const newErrors = {};
        
        if (data.errors && typeof data.errors === 'object') {
          // Map backend errors to form fields
          Object.keys(data.errors).forEach(key => {
            newErrors[key] = data.errors[key];
          });
        }
        
        // Also show general error message if available
        if (data.message && !data.errors) {
          newErrors.submit = data.message;
        } else if (data.message && Object.keys(newErrors).length === 0) {
          newErrors.submit = data.message;
        }
        
        setErrors(newErrors);
        // Aller à l'étape avec erreur
        if (newErrors.firstName || newErrors.lastName || newErrors.email || newErrors.phoneNumber) {
          setCurrentStep(0);
        } else if (newErrors.companyName || newErrors.legalForm || newErrors.address) {
          setCurrentStep(1);
        } else if (newErrors.meetingDate || newErrors.meetingTime) {
          setCurrentStep(2);
        }
        return;
      }
      
      setIsSubmitted(true);
    } catch (error) {
      setErrors(prev => ({
        ...prev,
        submit: error.message || t('agencyRegistration.submitError')
      }));
    } finally {
      setIsLoading(false);
    }
  };

  // Rendu du contenu par étape
  const renderStepContent = () => {
    switch (currentStep) {
      case 0:
        return (
          <>
            {/* Google Auto-Fill Button */}
            <div className="mb-6">
              <button
                type="button"
                onClick={handleGoogleAutoFill}
                disabled={isGoogleLoading}
                className="w-full flex items-center justify-center gap-3 py-3 px-4 border-2 border-gray-200 rounded-xl hover:bg-gray-50 hover:border-gray-300 transition font-medium text-gray-700 disabled:opacity-70 disabled:cursor-not-allowed bg-white"
              >
                {isGoogleLoading ? (
                  <>
                    <Loader2 className="w-5 h-5 animate-spin" />
                    <span>Chargement...</span>
                  </>
                ) : (
                  <>
                    <svg className="w-5 h-5" viewBox="0 0 24 24">
                      <path
                        fill="#4285F4"
                        d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
                      />
                      <path
                        fill="#34A853"
                        d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                      />
                      <path
                        fill="#FBBC05"
                        d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
                      />
                      <path
                        fill="#EA4335"
                        d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                      />
                    </svg>
                    Remplir avec Google
                  </>
                )}
              </button>
              {errors.googleAutoFill && (
                <p className="text-red-500 text-xs mt-2">{errors.googleAutoFill}</p>
              )}
              {googleSuccessMessage && (
                <p className="text-green-600 text-xs mt-2">{googleSuccessMessage}</p>
              )}
            </div>

            {/* First Name & Last Name */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {t('agencyRegistration.firstName')} <span className="text-red-500">*</span>
                </label>
                <div className="relative">
                  <User className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                  <input
                    type="text"
                    name="firstName"
                    value={formData.firstName}
                    onChange={handleChange}
                    className={`w-full pl-11 pr-4 py-3 rounded-xl border ${
                      errors.firstName ? 'border-red-500 bg-red-50' : 'border-gray-200'
                    } focus:border-[#27489E] focus:ring-2 focus:ring-[#27489E]/20 outline-none transition`}
                    placeholder={t('agencyRegistration.firstNamePlaceholder')}
                  />
                </div>
                {errors.firstName && (
                  <p className="text-red-500 text-xs mt-1">{errors.firstName}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {t('agencyRegistration.lastName')} <span className="text-red-500">*</span>
                </label>
                <div className="relative">
                  <User className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                  <input
                    type="text"
                    name="lastName"
                    value={formData.lastName}
                    onChange={handleChange}
                    className={`w-full pl-11 pr-4 py-3 rounded-xl border ${
                      errors.lastName ? 'border-red-500 bg-red-50' : 'border-gray-200'
                    } focus:border-[#27489E] focus:ring-2 focus:ring-[#27489E]/20 outline-none transition`}
                    placeholder={t('agencyRegistration.lastNamePlaceholder')}
                  />
                </div>
                {errors.lastName && (
                  <p className="text-red-500 text-xs mt-1">{errors.lastName}</p>
                )}
              </div>
            </div>

            {/* Email */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {t('agencyRegistration.email')} <span className="text-red-500">*</span>
              </label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  className={`w-full pl-11 pr-4 py-3 rounded-xl border ${
                    errors.email ? 'border-red-500 bg-red-50' : 'border-gray-200'
                  } focus:border-[#27489E] focus:ring-2 focus:ring-[#27489E]/20 outline-none transition`}
                  placeholder={t('agencyRegistration.emailPlaceholder')}
                />
              </div>
              {errors.email && (
                <p className="text-red-500 text-xs mt-1">{errors.email}</p>
              )}
            </div>

            {/* Phone Number */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                {t('agencyRegistration.phone')} <span className="text-red-500">*</span>
              </label>
              
              <div className="relative flex rounded-md shadow-sm">
                {/* Country Code (Static) */}
                <div className="inline-flex items-center px-3 rounded-l-md border border-r-0 border-gray-300 bg-gray-50 text-gray-500 sm:text-sm">
                  <span className="mr-2">🇲🇦</span> +212
                </div>
                
                {/* The Input Field */}
                <input
                  type="tel"
                  name="phoneNumber"
                  value={formData.phoneNumber}
                  ref={phoneInputRef}
                  placeholder="6 12 34 56 78"
                  maxLength={11} // Allow spaces but limit length
                  
                  // 1. CRITICAL: Clear error immediately on ANY change
                  onChange={handlePhoneChange}
                  
                  // 2. Validate ONLY when leaving the field
                  onBlur={handlePhoneBlur}
                  
                  // 3. Styling Logic
                  className={`flex-1 block w-full rounded-none rounded-r-md sm:text-sm focus:outline-none transition-all duration-200 p-3 border ${
                    phoneError
                      ? 'border-red-500 text-red-900 placeholder-red-300 focus:ring-red-500 focus:border-red-500 bg-red-50' // Error State
                      : highlightPhone 
                        ? 'border-orange-500 ring-2 ring-orange-100' // Google Highlight State
                        : 'border-gray-300 focus:ring-[#27489E] focus:border-[#27489E]' // Normal State
                  }`}
                />
                
                {/* Error Icon (Exclamation) */}
                {phoneError && (
                  <div className="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none">
                    <svg className="h-5 w-5 text-red-500" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                    </svg>
                  </div>
                )}
              </div>
              
              {/* Reserved Error Space (Fixed height to prevent layout shift) */}
              <div className="h-6 mt-2">
                {errors.phoneNumber && (
                  <p className="text-sm text-red-600" id="phone-error">
                    {errors.phoneNumber}
                  </p>
                )}
                {phoneError && !errors.phoneNumber && (
                  <p className="text-sm text-red-600" id="phone-error">
                    Le numéro doit contenir exactement 9 chiffres.
                  </p>
                )}
              </div>
            </div>

            {/* Password */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Mot de passe <span className="text-red-500">*</span>
              </label>
              <div className="relative">
                <input
                  type={showPassword ? 'text' : 'password'}
                  name="password"
                  value={formData.password}
                  onChange={handleChange}
                  className={`w-full pl-4 ${passwordError ? 'pr-20' : 'pr-10'} py-3 rounded-xl border transition-colors duration-200 ${
                    passwordError
                      ? 'border-red-300 text-red-900 placeholder-red-300 focus:border-red-500 focus:ring-red-500 bg-red-50'
                      : errors.password
                        ? 'border-red-500 bg-red-50'
                        : 'border-gray-300 focus:border-[#27489E] focus:ring-[#27489E]'
                  } focus:ring-2 focus:ring-[#27489E]/20 outline-none`}
                  placeholder="Minimum 8 caractères"
                />
                {/* Eye icon button */}
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute inset-y-0 right-0 flex items-center pr-3 text-gray-400 hover:text-gray-600 z-10"
                >
                  {showPassword ? (
                    <EyeOff className="h-5 w-5" />
                  ) : (
                    <Eye className="h-5 w-5" />
                  )}
                </button>
                {/* Alert icon (only if passwordError) */}
                {passwordError && (
                  <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-10">
                    <AlertCircle className="h-5 w-5 text-red-500" />
                  </div>
                )}
              </div>
              {/* Reserved Error Space (Fixed height to prevent layout shift) */}
              <div className="h-6 mt-2">
                {errors.password && (
                  <p className="text-sm text-red-600" id="password-error">
                    {errors.password}
                  </p>
                )}
                {passwordError && !errors.password && (
                  <p className="text-sm text-red-600" id="password-error">
                    Le mot de passe doit contenir au moins 8 caractères.
                  </p>
                )}
              </div>
            </div>

            {/* Confirm Password */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Confirmer le mot de passe <span className="text-red-500">*</span>
              </label>
              <div className="relative">
                <input
                  type={showPassword ? 'text' : 'password'}
                  name="confirmPassword"
                  value={formData.confirmPassword}
                  onChange={handleChange}
                  className={`w-full pl-4 ${confirmPasswordError ? 'pr-20' : 'pr-10'} py-3 rounded-xl border transition-colors duration-200 ${
                    confirmPasswordError
                      ? 'border-red-300 text-red-900 placeholder-red-300 focus:border-red-500 focus:ring-red-500 bg-red-50'
                      : errors.confirmPassword
                        ? 'border-red-500 bg-red-50'
                        : 'border-gray-300 focus:border-[#27489E] focus:ring-[#27489E]'
                  } focus:ring-2 focus:ring-[#27489E]/20 outline-none`}
                  placeholder="Répétez le mot de passe"
                />
                {/* Eye icon button */}
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute inset-y-0 right-0 flex items-center pr-3 text-gray-400 hover:text-gray-600 z-10"
                >
                  {showPassword ? (
                    <EyeOff className="h-5 w-5" />
                  ) : (
                    <Eye className="h-5 w-5" />
                  )}
                </button>
                {/* Alert icon (only if confirmPasswordError) */}
                {confirmPasswordError && (
                  <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-10">
                    <AlertCircle className="h-5 w-5 text-red-500" />
                  </div>
                )}
              </div>
              {/* Reserved Error Space (Fixed height to prevent layout shift) */}
              <div className="h-6 mt-2">
                {errors.confirmPassword && (
                  <p className="text-sm text-red-600" id="confirm-password-error">
                    {errors.confirmPassword}
                  </p>
                )}
                {confirmPasswordError && !errors.confirmPassword && (
                  <p className="text-sm text-red-600" id="confirm-password-error">
                    Les mots de passe ne correspondent pas.
                  </p>
                )}
              </div>
            </div>
          </>
        );

      case 1:
        return (
          <>
            {/* Company Name */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {t('agencyRegistration.companyName')} <span className="text-red-500">*</span>
              </label>
              <div className="relative">
                <Building2 className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                <input
                  type="text"
                  name="companyName"
                  value={formData.companyName}
                  onChange={handleChange}
                  className={`w-full pl-11 pr-4 py-3 rounded-xl border ${
                    errors.companyName ? 'border-red-500 bg-red-50' : 'border-gray-200'
                  } focus:border-[#27489E] focus:ring-2 focus:ring-[#27489E]/20 outline-none transition`}
                  placeholder={t('agencyRegistration.companyNamePlaceholder')}
                />
              </div>
              {errors.companyName && (
                <p className="text-red-500 text-xs mt-1">{errors.companyName}</p>
              )}
            </div>

            {/* Legal Form */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {t('agencyRegistration.legalForm')} <span className="text-red-500">*</span>
              </label>
              <div className="relative">
                <Scale className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400 z-10" />
                <select
                  name="legalForm"
                  value={formData.legalForm}
                  onChange={handleChange}
                  className={`w-full pl-11 pr-4 py-3 rounded-xl border appearance-none bg-white ${
                    errors.legalForm ? 'border-red-500 bg-red-50' : 'border-gray-200'
                  } focus:border-[#27489E] focus:ring-2 focus:ring-[#27489E]/20 outline-none transition cursor-pointer`}
                >
                  <option value="">{t('agencyRegistration.legalFormPlaceholder')}</option>
                  {legalForms.map(form => (
                    <option key={form.value} value={form.value}>
                      {form.label}
                    </option>
                  ))}
                </select>
                <div className="absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none">
                  <svg className="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </div>
              </div>
              {errors.legalForm && (
                <p className="text-red-500 text-xs mt-1">{errors.legalForm}</p>
              )}
            </div>

            {/* Address */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Adresse de siège social <span className="text-red-500">*</span>
              </label>
              <div className="relative">
                <MapPin className="absolute left-3 top-3 w-5 h-5 text-gray-400" />
                <textarea
                  name="address"
                  value={formData.address}
                  onChange={handleChange}
                  rows="3"
                  className={`w-full pl-11 pr-4 py-3 rounded-xl border resize-none ${
                    errors.address ? 'border-red-500 bg-red-50' : 'border-gray-200'
                  } focus:border-[#27489E] focus:ring-2 focus:ring-[#27489E]/20 outline-none transition`}
                  placeholder={t('agencyRegistration.addressPlaceholder')}
                />
              </div>
              {errors.address && (
                <p className="text-red-500 text-xs mt-1">{errors.address}</p>
              )}
            </div>

            {/* Logo Upload */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {t('agencyRegistration.logo')}
              </label>
              {formData.logo ? (
                <div className="flex items-center justify-between p-4 bg-gray-50 border border-blue-200 rounded-lg shadow-sm">
                  <div className="flex items-center space-x-3 overflow-hidden flex-1 min-w-0">
                    {/* File Icon */}
                    <div className="p-2 bg-blue-100 rounded-lg flex-shrink-0">
                      <ImageIcon className="w-6 h-6 text-blue-600" />
                    </div>
                    {/* File Details */}
                    <div className="flex flex-col overflow-hidden min-w-0">
                      <span className="text-sm font-medium text-gray-900 truncate">
                        {formData.logo.name}
                      </span>
                      <span className="text-xs text-gray-500">
                        {(formData.logo.size / 1024).toFixed(0)} KB
                      </span>
                    </div>
                  </div>
                  {/* Delete Button */}
                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      removeLogo();
                    }}
                    className="p-2 text-gray-400 hover:text-red-500 transition-colors flex-shrink-0 ml-3"
                    title="Supprimer le fichier"
                  >
                    <X className="w-5 h-5" />
                  </button>
                </div>
              ) : (
                <div className="border-2 border-dashed border-gray-300 rounded-xl p-6 bg-gray-50">
                  <button
                    type="button"
                    onClick={() => fileInputRef.current?.click()}
                    className="w-full flex items-center justify-center gap-3 px-4 py-3 bg-white border-2 border-gray-200 rounded-xl hover:border-[#27489E] hover:bg-[#27489E]/5 transition"
                  >
                    <ImageIcon className="w-5 h-5 text-[#27489E]" />
                    <span className="text-sm font-medium text-gray-700">
                      {t('agencyRegistration.selectPhoto')}
                    </span>
                  </button>
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/*"
                    onChange={handleFileSelect}
                    className="hidden"
                  />
                </div>
              )}
              {errors.logo && (
                <p className="text-red-500 text-xs mt-1">{errors.logo}</p>
              )}
            </div>
          </>
        );

      case 2:
        // Step 3: Appel de Validation (Scheduling)
        return (
          <>
            {/* Header */}
            <div className="mb-6">
              <h3 className="text-xl font-semibold text-gray-800 mb-2">
                Quand pouvons-nous vous appeler ?
              </h3>
              <p className="text-gray-600 text-sm">
                Un expert Carvy validera votre dossier pendant cet appel.
              </p>
            </div>

            {/* Date Selector */}
            <div className="mb-6">
              <label className="block text-sm font-medium text-gray-700 mb-3">
                Sélectionnez une date <span className="text-red-500">*</span>
              </label>
              <div className="flex gap-3 overflow-x-auto pb-2 [&::-webkit-scrollbar]:hidden [-ms-overflow-style:none] [scrollbar-width:none]">
                {(() => {
                  const dates = [];
                  const today = new Date();
                  today.setHours(0, 0, 0, 0); // Normaliser l'heure à minuit
                  let currentDate = new Date(today);
                  
                  // Générer exactement 5 dates en excluant les dimanches
                  while (dates.length < 5) {
                    const dayOfWeek = currentDate.getDay();
                    // Si ce n'est pas un dimanche (0), l'ajouter
                    if (dayOfWeek !== 0) {
                      dates.push(new Date(currentDate));
                    }
                    // Passer au jour suivant
                    currentDate.setDate(currentDate.getDate() + 1);
                  }
                  
                  return dates.map((date, index) => {
                    const dayNames = ['DIM', 'LUN', 'MAR', 'MER', 'JEU', 'VEN', 'SAM'];
                    const dayName = dayNames[date.getDay()];
                    const dayNumber = date.getDate();
                    const dateStr = date.toISOString().split('T')[0];
                    const isSelected = formData.meetingDate === dateStr;
                    
                    return (
                      <button
                        key={index}
                        type="button"
                        onClick={() => setFormData(prev => ({ ...prev, meetingDate: dateStr }))}
                        className={`
                          flex flex-col items-center justify-center min-w-[80px] p-4 rounded-xl border-2 transition-all
                          ${isSelected
                            ? 'bg-[#27489E] text-white border-[#27489E] shadow-lg scale-105'
                            : 'bg-white text-gray-700 border-gray-200 hover:border-[#27489E] hover:shadow-md'
                          }
                        `}
                      >
                        <span className="text-xs font-medium mb-1">{dayName}</span>
                        <span className={`text-2xl font-bold ${isSelected ? 'text-white' : 'text-gray-900'}`}>
                          {dayNumber}
                        </span>
                      </button>
                    );
                  });
                })()}
              </div>
              {errors.meetingDate && (
                <p className="text-red-500 text-xs mt-2">{errors.meetingDate}</p>
              )}
            </div>

            {/* Time Selector */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-3">
                Sélectionnez un horaire <span className="text-red-500">*</span>
              </label>
              <div className="flex gap-3 overflow-x-auto pb-2 [&::-webkit-scrollbar]:hidden [-ms-overflow-style:none] [scrollbar-width:none]">
                {['09:00 - 12:00', '12:00 - 15:00', '15:00 - 18:00'].map((timeInterval) => {
                  const isSelected = formData.meetingTime === timeInterval;
                  return (
                    <button
                      key={timeInterval}
                      type="button"
                      onClick={() => setFormData(prev => ({ ...prev, meetingTime: timeInterval }))}
                      className={`
                        flex flex-col items-center justify-center min-w-[140px] p-4 rounded-xl border-2 transition-all
                        ${isSelected
                          ? 'bg-[#27489E] text-white border-[#27489E] shadow-lg scale-105'
                          : 'bg-white text-gray-700 border-gray-200 hover:border-[#27489E] hover:shadow-md'
                        }
                      `}
                    >
                      <span className={`text-sm font-semibold ${isSelected ? 'text-white' : 'text-gray-900'}`}>
                        {timeInterval}
                      </span>
                    </button>
                  );
                })}
              </div>
              {errors.meetingTime && (
                <p className="text-red-500 text-xs mt-2">{errors.meetingTime}</p>
              )}
            </div>
          </>
        );

      case 3:
        return (
          <>
            {/* Error Message */}
            {errors.submit && (
              <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-xl flex items-center gap-2 mb-6">
                <AlertCircle className="w-5 h-5 flex-shrink-0" />
                <span className="text-sm">{errors.submit}</span>
              </div>
            )}

            {/* Info Box */}
            <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 flex items-start gap-3 mb-6">
              <div className="w-6 h-6 bg-blue-500 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                <span className="text-white text-xs font-bold">i</span>
              </div>
              <p className="text-sm text-blue-800">
                {t('agencyRegistration.infoMessage')}
              </p>
            </div>

            {/* Summary Card - Personal Info */}
            <div className="mb-6">
              <h3 className="text-lg font-semibold text-gray-800 mb-4">Informations du Gérant</h3>
              <div className="bg-gray-50 rounded-xl p-6 space-y-3">
                <div className="flex justify-between items-center">
                  <span className="text-gray-600">Prénom:</span>
                  <span className="font-medium text-gray-900">{formData.firstName || '-'}</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-gray-600">Nom:</span>
                  <span className="font-medium text-gray-900">{formData.lastName || '-'}</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-gray-600">Email:</span>
                  <span className="font-medium text-gray-900">{formData.email || '-'}</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-gray-600">Téléphone:</span>
                  <div className="flex items-center gap-2">
                    <span className="font-medium text-gray-900">{formData.phoneNumber || '-'}</span>
                    <button
                      type="button"
                      onClick={handleEditPhone}
                      className="text-xs text-blue-600 hover:text-blue-800 underline font-medium transition-colors"
                    >
                      Modifier
                    </button>
                  </div>
                </div>
              </div>
              
              {/* Bannière d'information téléphone */}
              <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 flex items-start gap-3 mt-4">
                <div className="flex-shrink-0 mt-0.5">
                  <Info className="w-5 h-5 text-red-600" />
                </div>
                <div className="flex-1">
                  <p className="text-sm text-red-600">
                    Un expert Carvy vous contactera sur ce numéro pour valider votre dossier.
                  </p>
                </div>
              </div>
            </div>

            {/* Summary Card - Agency Info */}
            <div className="mb-6">
              <h3 className="text-lg font-semibold text-gray-800 mb-4">Informations de l'Agence</h3>
              <div className="bg-gray-50 rounded-xl p-6 space-y-3">
                <div className="flex justify-between items-center">
                  <span className="text-gray-600">Nom de l'entreprise:</span>
                  <span className="font-medium text-gray-900">{formData.companyName || '-'}</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-gray-600">Forme juridique:</span>
                  <span className="font-medium text-gray-900">
                    {legalForms.find(f => f.value === formData.legalForm)?.label || '-'}
                  </span>
                </div>
                <div className="flex justify-between items-start">
                  <span className="text-gray-600">Adresse:</span>
                  <span className="font-medium text-gray-900 text-right max-w-xs">{formData.address || '-'}</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-gray-600">Logo:</span>
                  <span className="font-medium text-gray-900">{formData.logo ? 'Oui' : 'Non'}</span>
                </div>
              </div>
            </div>

            {/* Summary Card - Appointment Info */}
            {formData.meetingDate && formData.meetingTime && (
              <div className="mb-6">
                <h3 className="text-lg font-semibold text-gray-800 mb-4">Appel de Validation</h3>
                <div className="bg-gray-50 rounded-xl p-6 space-y-3">
                  <div className="flex justify-between items-center">
                    <span className="text-gray-600">Date:</span>
                    <span className="font-medium text-gray-900">
                      {(() => {
                        if (!formData.meetingDate) return '-';
                        const date = new Date(formData.meetingDate);
                        const dayNames = ['Dimanche', 'Lundi', 'Mardi', 'Mercredi', 'Jeudi', 'Vendredi', 'Samedi'];
                        const monthNames = ['janvier', 'février', 'mars', 'avril', 'mai', 'juin', 'juillet', 'août', 'septembre', 'octobre', 'novembre', 'décembre'];
                        return `${dayNames[date.getDay()]} ${date.getDate()} ${monthNames[date.getMonth()]}`;
                      })()}
                    </span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-gray-600">Horaire:</span>
                    <span className="font-medium text-gray-900">{formData.meetingTime || '-'}</span>
                  </div>
                </div>
              </div>
            )}

            {/* Terms Checkbox */}
            <div className="mb-6">
              <label className="flex items-start gap-3 cursor-pointer">
                <div className="relative mt-0.5 flex-shrink-0">
                  <input
                    type="checkbox"
                    id="acceptTermsAgency"
                    checked={acceptedTerms}
                    onChange={(e) => {
                      e.stopPropagation();
                      setAcceptedTerms(e.target.checked);
                    }}
                    required
                    className="sr-only"
                  />
                  <div 
                    className={`w-5 h-5 rounded border-2 flex items-center justify-center transition cursor-pointer ${
                      acceptedTerms 
                        ? 'bg-[#27489E] border-[#27489E]' 
                        : 'border-gray-300 bg-white hover:border-[#27489E]'
                    }`}
                    onClick={(e) => {
                      e.preventDefault();
                      setAcceptedTerms(!acceptedTerms);
                    }}
                  >
                    {acceptedTerms && <Check className="w-3 h-3 text-white" />}
                  </div>
                </div>
                <span 
                  className="text-sm text-gray-600 select-none"
                  onClick={(e) => {
                    e.preventDefault();
                    setAcceptedTerms(!acceptedTerms);
                  }}
                >
                  {t('agencyRegistration.termsText')}
                </span>
              </label>
            </div>

            {/* Submit Button */}
            <button
              type="button"
              onClick={handleSubmit}
              disabled={isLoading || !acceptedTerms}
              className="w-full bg-gradient-to-r from-[#27489E] to-[#1e3a7d] text-white py-4 rounded-xl font-semibold flex items-center justify-center gap-2 hover:shadow-lg hover:shadow-[#27489E]/30 transition-all disabled:opacity-70 disabled:cursor-not-allowed"
            >
              {isLoading ? (
                <>
                  <Loader2 className="w-5 h-5 animate-spin" />
                  <span>Envoi en cours...</span>
                </>
              ) : (
                <>
                  {t('agencyRegistration.submitButton')}
                  <ArrowRight className="w-5 h-5" />
                </>
              )}
            </button>
          </>
        );

      default:
        return null;
    }
  };

  if (isSubmitted) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <div className="max-w-md w-full">
          {/* Main Container - Centered on light gray background */}
          <div className="flex flex-col items-center justify-center min-h-[60vh] bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
            {/* The Success Card */}
            <div className="text-center">
              {/* 1. The HERO ICON (Animated Orange Check) */}
              <div className="flex justify-center animate-bounce">
                {/* A circle with an orange gradient */}
                <div className="rounded-full bg-gradient-to-br from-orange-400 to-orange-600 p-5 shadow-lg shadow-orange-200">
                  {/* Standard Heroicon Check */}
                  <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={3} stroke="currentColor" className="w-16 h-16 text-white">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M4.5 12.75l6 6 9-13.5" />
                  </svg>
                </div>
              </div>

              {/* 2. Typography */}
              <div>
                {/* Title with Orange accent word */}
                <h2 className="mt-6 text-3xl font-extrabold text-gray-900">
                  Demande reçue & <span className="text-orange-600">Rendez-vous confirmé !</span>
                </h2>
                {/* Body Text - More informative */}
                <p className="mt-4 text-lg text-gray-500">
                  Votre dossier a été transmis. Un expert Carvy vous contactera à l'heure convenue pour finaliser votre activation.
                </p>
              </div>

              {/* Meeting Confirmation Card */}
              {formData.meetingDate && formData.meetingTime && (
                <div className="mt-6 bg-orange-50 border border-orange-100 rounded-xl p-6 flex flex-col items-center animate-fadeIn">
                  <span className="text-xs font-bold text-orange-600 uppercase tracking-widest mb-2">
                    APPEL D'ACTIVATION
                  </span>
                  {/* Dynamic Date & Time */}
                  <div className="text-center">
                    <p className="text-2xl font-bold text-gray-900">
                      {(() => {
                        if (!formData.meetingDate) return "Date à confirmer";
                        const date = new Date(formData.meetingDate);
                        const dayNames = ['Dimanche', 'Lundi', 'Mardi', 'Mercredi', 'Jeudi', 'Vendredi', 'Samedi'];
                        const monthNames = ['janvier', 'février', 'mars', 'avril', 'mai', 'juin', 'juillet', 'août', 'septembre', 'octobre', 'novembre', 'décembre'];
                        return `${dayNames[date.getDay()]} ${date.getDate()} ${monthNames[date.getMonth()]}`;
                      })()}
                    </p>
                    <p className="text-lg font-medium text-orange-600">
                      à {formData.meetingTime || "--:--"}
                    </p>
                  </div>
                </div>
              )}

              {/* Support Message */}
              <div className="my-6 text-center">
                <p className="text-sm text-gray-600">
                  Vous avez des questions ou besoin d'assistance ? Contactez-nous au{' '}
                  <span className="font-bold text-gray-900">+212 660-060079</span>
                  <br />
                </p>
              </div>

              {/* 3. Action Button (Orange Theme) */}
              <div className="mt-8">
                <button
                  onClick={() => navigate(ROUTES.HOME)}
                  className="w-full flex justify-center py-3 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-orange-600 hover:bg-orange-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-orange-500 transition-colors duration-200"
                >
                  Retour à l'accueil
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 py-8 px-4">
      <div className="w-full max-w-5xl mx-auto">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="flex justify-center mb-4">
            <Logo size="text-4xl" carColor="text-[#27489E]" vyColor="text-[#F5A623]" />
          </div>
          <h1 className="text-3xl font-bold text-gray-800 mb-2">
            {t('agencyRegistration.title')}
          </h1>
          <p className="text-gray-600">
            {t('agencyRegistration.subtitle')}
          </p>
        </div>

        {/* Main Container: Previous Button + Card + Next Button (Desktop) */}
        <div className="flex flex-col md:flex-row items-center justify-center gap-4 md:gap-8 w-full max-w-6xl mx-auto">
          {/* 1. LEFT SIDE: Floating Previous Button (Desktop) - Vertically Centered */}
          {currentStep > 0 && (
            <div className="hidden md:flex flex-col items-center gap-2 flex-shrink-0">
              {/* Label */}
              <span className="text-sm font-medium transition-colors text-[#F5A623]">
                Précédent
              </span>
              
              {/* Button */}
              <button
                type="button"
                onClick={handlePrevious}
                disabled={isLoading}
                className={`
                  flex items-center justify-center w-14 h-14 rounded-full font-semibold transition-all shadow-lg
                  ${isLoading
                    ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                    : 'bg-gradient-to-r from-[#27489E] to-[#1e3a7d] text-white hover:shadow-xl hover:scale-110'
                  }
                `}
                title="Précédent"
              >
                <ChevronLeft className="w-6 h-6" />
              </button>
            </div>
          )}

          {/* 2. CENTER: Wizard Card */}
          <div className="w-full max-w-2xl flex-shrink-0">
            <Wizard
              currentStep={currentStep}
              setCurrentStep={setCurrentStep}
              steps={steps}
              onNext={handleNext}
              onPrevious={handlePrevious}
              canGoNext={true}
              isSubmitting={isLoading}
              showMobileNext={true}
            >
              {renderStepContent()}
            </Wizard>
          </div>

          {/* 3. RIGHT SIDE: Floating Next Button (Desktop) - Vertically Centered */}
          {currentStep < steps.length - 1 && (
            <div className="hidden md:flex flex-col items-center gap-2 flex-shrink-0">
              {/* Label */}
              <span className="text-sm font-medium transition-colors text-[#F5A623]">
                Suivant
              </span>
              
              {/* Button */}
              <button
                type="button"
                onClick={handleNext}
                disabled={isLoading}
                className={`
                  flex items-center justify-center w-14 h-14 rounded-full font-semibold transition-all shadow-lg
                  ${isLoading
                    ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                    : 'bg-gradient-to-r from-[#27489E] to-[#1e3a7d] text-white hover:shadow-xl hover:scale-110'
                  }
                `}
                title="Suivant"
              >
                <ChevronRight className="w-6 h-6" />
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default AgencyRegistration;

